import type { ReactElement, ReactNode } from "react";
import React from "react";
import { Alert, Button, Snackbar as Snack } from "@mui/material";
import Slide from "@mui/material/Slide";
import type {
  SVG,
  StyledComponentBase,
} from "@/components/component-base.types";
import classNames from "classnames";
import type { MessageBoxType as SnackbarType } from "@/types/shared.types";
import styles from "./snackbar.module.scss";
import { iconMap } from "../message-box/message-box";

export interface SnackbarProps extends StyledComponentBase {
  /**
   * Clickable text describing an action for a user to take from the snackbar
   */
  actionText?: string;
  /**
   * Action to take when action text is clicked
   */
  onActionClick?: () => void;
  /**
   * Text to display on button
   */
  /**
   * Determines whether an X icon is present to close the snackbar
   * Snackbars can otherwise be closed by hitting the Escape key
   */
  hasCloseIcon?: boolean;
  /**
   * Message of the Snackbar. Should be a string
   */
  message?: string | React.ReactNode;
  /**
   * State variable that determines if the snackbar is open
   */
  open: boolean;
  /**
   * Function that handles the opening and closing of the snackbar
   */
  onOpenChange: (arg: boolean) => void;
  /** SnackbarType determines coloring and icons. */
  snackbarType?: SnackbarType;
  /**
   * Optional custom content to render inside the Snackbar. If provided, this will override the default message and action layout.
   * Accepts any valid React node (JSX, string, etc).
   */
  children?: ReactNode;
  /**
   * Whether to show the icon on the left side of the snackbar.
   * By default, icons are shown for all snackbar types except "highlight".
   * Set this to false to hide the icon.
   */
  showLeftIcon?: boolean;
  /** Optional icon override. Pass false to suppress the type's default icon. */
  icon?: SVG | false;
  /** Time in milliseconds before the snackbar closes automatically. */
  autoHideDuration?: number;
}

export const getIcon = (
  type: SnackbarType,
  icon?: SVG | false,
): ReactElement | null => {
  const Svg = icon === undefined ? iconMap[type] : icon;
  if (!Svg) return null;
  return <Svg className={classNames(styles.icon, styles[`${type}Icon`])} />;
};

export const Snackbar = (iProps: SnackbarProps) => {
  const {
    actionText,
    hasCloseIcon = false,
    onActionClick,
    message,
    open,
    onOpenChange,
    snackbarType = "highlight",
    className,
    children,
    showLeftIcon = true,
    icon,
    autoHideDuration = 10000,
  } = iProps;

  const action = (
    <Button
      className={classNames(
        styles.actionButton,
        snackbarType === "highlight" ? styles.actionHighlight : styles.action,
      )}
      onClick={onActionClick}
    >
      {actionText?.toUpperCase()}
    </Button>
  );

  const handleClose = (
    _event: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    onOpenChange(false);
  };

  const long =
    actionText &&
    actionText &&
    (actionText.length > 10 ||
      (typeof message === "string" && message.length > 60));

  return (
    <Slide direction="up" in={open}>
      <Snack
        className={classNames(styles.snackbarRoot, className)}
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
      >
        <Alert
          className={classNames(
            long ? styles.twoActions : styles.oneAction,
            styles.alertRoot,
            styles[snackbarType],
          )}
          icon={
            showLeftIcon && snackbarType !== "highlight"
              ? getIcon(snackbarType, icon)
              : false
          }
          onClose={hasCloseIcon ? handleClose : undefined}
          variant={`${snackbarType}_solid`}
        >
          {children || (
            <div className={long ? styles.longContent : styles.content}>
              <div className={styles.message}>{message}</div>
              {actionText ? action : null}
            </div>
          )}
        </Alert>
      </Snack>
    </Slide>
  );
};
